import { firestore } from '../database/firebase';
import { collection, doc, getDoc, getDocs, query, where, orderBy } from 'firebase/firestore';

const BUSINESS_HEALTH_CATEGORY_LABELS = {
  strategy: 'Strategy & Vision',
  financial: 'Financial Health',
  sales: 'Sales & Marketing',
  operations: 'Operations & Processes',
  customer: 'Customer Experience',
  leadership: 'Leadership & Team',
  compliance: 'Compliance & Risk',
  innovation: 'Innovation & Growth',
};

const formatCategoryLabel = key => {
  if (!key) return '';
  if (BUSINESS_HEALTH_CATEGORY_LABELS[key]) return BUSINESS_HEALTH_CATEGORY_LABELS[key];

  const spaced = key
    .replace(/([A-Z])/g, ' $1')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  if (!spaced) return '';
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

const describeScore = score => {
  if (score >= 80) return 'Excellent';
  if (score >= 60) return 'Stable';
  if (score >= 40) return 'Developing';
  if (score > 0) return 'At Risk';
  return 'Not Started';
};

const normalizeDateValue = value => {
  if (!value) return null;
  if (typeof value === 'string') return value;
  if (value.toDate) return value.toDate().toISOString();
  if (value.toMillis) return new Date(value.toMillis()).toISOString();
  return value;
};

export async function generateMonthlyCoachingReport({ clientId, month, year, coachId = null }) {
  if (!clientId) throw new Error('Client ID is required');

  const reportDate = new Date(year, month - 1, 1); // First day of the month
  const reportMonthName = reportDate.toLocaleString('default', { month: 'long', year: 'numeric' });

  try {
    const clientDoc = await getDoc(doc(firestore, 'clients', clientId));
    const clientData = clientDoc.exists() ? clientDoc.data() : null;

    if (!clientData) {
      throw new Error('Client not found');
    }

    // Fetch company profile if exists
    let companyProfile = null;
    try {
      const companyDoc = await getDoc(doc(firestore, 'companyProfiles', clientId));
      if (companyDoc.exists()) {
        companyProfile = companyDoc.data();
      }
    } catch (error) {
      console.warn('Company profile not found:', error);
    }

    // Fetch entrepreneur profile if exists
    let entrepreneurProfileDoc = null;
    try {
      const entrepreneurDoc = await getDoc(doc(firestore, 'entrepreneurProfiles', clientId));
      if (entrepreneurDoc.exists()) {
        entrepreneurProfileDoc = entrepreneurDoc.data();
      }
    } catch (error) {
      console.warn('Entrepreneur profile not found:', error);
    }

    // Fetch Business Health Assessment if exists
    let businessHealthAssessment = null;
    try {
      const businessHealthDoc = await getDoc(doc(firestore, 'businessHealthAssessments', clientId));
      if (businessHealthDoc.exists()) {
        businessHealthAssessment = businessHealthDoc.data();
      }
    } catch (error) {
      console.warn('Business health assessment not found:', error);
    }

    // Fetch Product Development submissions
    const productDevSubmissions = await getDocs(
      query(
        collection(firestore, 'productDevelopmentSubmissions'),
        where('ownerId', '==', clientId),
        orderBy('createdAt', 'desc')
      )
    );
    const submissions = productDevSubmissions.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));

    // Fetch Document Bank documents
    const documentBankDocs = await getDocs(
      query(
        collection(firestore, 'documentBank'),
        where('ownerId', '==', clientId),
        orderBy('uploadedAt', 'desc')
      )
    );
    const documents = documentBankDocs.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));

    // Fetch Commercialisation Readiness Checklist
    let readinessChecklist = null;
    try {
      const readinessDoc = await getDoc(
        doc(firestore, 'commercialisation', clientId, 'readinessChecklist', clientId)
      );
      if (readinessDoc.exists()) {
        readinessChecklist = readinessDoc.data();
      }
    } catch (error) {
      console.warn('Readiness checklist not found:', error);
    }

    // Fetch GTM Strategy Plan
    let gtmPlan = null;
    try {
      const gtmDoc = await getDoc(
        doc(firestore, 'commercialisation', clientId, 'gtmPlan', clientId)
      );
      if (gtmDoc.exists()) {
        const gtmData = gtmDoc.data();
        gtmPlan = {
          progress: gtmData.progress || 0,
          currentStep: gtmData.currentStep || 0,
          isDraft: gtmData.isDraft !== false,
          stepsCompleted: gtmData.steps?.filter(s => s.completed).length || 0,
          totalSteps: gtmData.steps?.length || 0,
          lastUpdated: normalizeDateValue(gtmData.updatedAt || gtmData.lastUpdated),
        };
      }
    } catch (error) {
      console.warn('GTM plan not found:', error);
    }

    // Fetch Launch Checklist
    let launchChecklist = null;
    try {
      const launchDoc = await getDoc(
        doc(firestore, 'commercialisation', clientId, 'launchChecklist', clientId)
      );
      if (launchDoc.exists()) {
        launchChecklist = launchDoc.data();
      }
    } catch (error) {
      console.warn('Launch checklist not found:', error);
    }

    // Calculate statistics
    const stats = {
      totalSubmissions: submissions.length,
      submissionsThisMonth: submissions.filter(s => {
        const createdAt = s.createdAt?.toMillis
          ? new Date(s.createdAt.toMillis())
          : s.createdAt
            ? new Date(s.createdAt)
            : null;
        return createdAt && createdAt.getMonth() === month - 1 && createdAt.getFullYear() === year;
      }).length,
      totalDocuments: documents.length,
      documentsThisMonth: documents.filter(d => {
        const uploadedAt = d.uploadedAt?.toMillis
          ? new Date(d.uploadedAt.toMillis())
          : d.uploadedAt
            ? new Date(d.uploadedAt)
            : null;
        return (
          uploadedAt && uploadedAt.getMonth() === month - 1 && uploadedAt.getFullYear() === year
        );
      }).length,
      readinessScore: readinessChecklist?.score || 0,
      gtmProgress: gtmPlan?.progress || 0,
      launchScore: launchChecklist?.score || 0,
      entrepreneurScore: entrepreneurProfileDoc?.overallScore || entrepreneurProfileDoc?.score || 0,
      businessHealthScore: businessHealthAssessment?.overallScore || 0,
    };

    const entrepreneurProfile = entrepreneurProfileDoc
      ? (() => {
          const categoryBlueprint = [
            {
              key: 'personality',
              label: 'Personality',
              source: entrepreneurProfileDoc.personality,
            },
            { key: 'mindset', label: 'Mindset', source: entrepreneurProfileDoc.mindset },
            {
              key: 'capabilities',
              label: 'Capabilities',
              source: entrepreneurProfileDoc.capabilities,
            },
            {
              key: 'businessStage',
              label: 'Business Profiling',
              source: entrepreneurProfileDoc.businessStage,
            },
          ];

          const categories = categoryBlueprint.map(category => ({
            key: category.key,
            label: category.label,
            score: category.source?.score || 0,
            summary: category.source?.notes || '',
            highlights: category.source?.strengths || [],
          }));

          const overallScore =
            entrepreneurProfileDoc.overallScore || entrepreneurProfileDoc.score || 0;

          return {
            overallScore,
            lastUpdated: normalizeDateValue(entrepreneurProfileDoc.lastUpdated),
            status: describeScore(overallScore),
            categories,
            focusAreas: categories
              .filter(category => category.score > 0 && category.score < 60)
              .map(category => category.label),
            standoutAreas: categories
              .filter(category => category.score >= 70)
              .map(category => category.label),
            details: {
              personality: entrepreneurProfileDoc.personality || {},
              mindset: entrepreneurProfileDoc.mindset || {},
              capabilities: entrepreneurProfileDoc.capabilities || {},
              businessStage: entrepreneurProfileDoc.businessStage || {},
            },
            notes: entrepreneurProfileDoc.notes || '',
          };
        })()
      : null;

    const businessHealth = businessHealthAssessment
      ? (() => {
          const categoryScores = Object.entries(businessHealthAssessment.categoryScores || {}).map(
            ([key, score]) => ({
              key,
              label: formatCategoryLabel(key),
              score: score || 0,
            })
          );
          const overallScore = businessHealthAssessment.overallScore || 0;

          return {
            overallScore,
            lastUpdated: normalizeDateValue(businessHealthAssessment.lastUpdated),
            status: describeScore(overallScore),
            categories: categoryScores,
            focusAreas: categoryScores
              .filter(category => category.score > 0 && category.score < 60)
              .map(category => category.label),
          };
        })()
      : null;

    // Compile the report
    const report = {
      reportMetadata: {
        generatedAt: new Date().toISOString(),
        reportMonth: month,
        reportYear: year,
        reportMonthName,
        clientId,
        coachId,
      },
      clientInfo: {
        name: `${clientData.firstname || ''} ${clientData.lastname || ''}`.trim(),
        email: clientData.email || '',
        organization:
          clientData.organization ||
          clientData.companyName ||
          clientData.organizationName ||
          companyProfile?.name ||
          '',
        phone: clientData.phone || companyProfile?.phone || '',
        location: clientData.location || companyProfile?.location || '',
        businessStage: clientData.businessStage || '',
        status: clientData.status || '',
        assignedCoach: clientData.assignedCoachName || clientData.assignedEmail || '',
        joinedDate: clientData.createdAt
          ? new Date(
              clientData.createdAt.toMillis ? clientData.createdAt.toMillis() : clientData.createdAt
            ).toLocaleDateString()
          : 'Unknown',
      },
      companyProfile: companyProfile
        ? {
            name: companyProfile.name || '',
            isLaunched: companyProfile.isLaunched || false,
            launchDate: companyProfile.launchDate || null,
            website: companyProfile.website || '',
            description: companyProfile.description || '',
          }
        : null,
      entrepreneurProfile,
      businessHealth,
      productDevelopment: {
        totalSubmissions: stats.totalSubmissions,
        submissionsThisMonth: stats.submissionsThisMonth,
        recentSubmissions: submissions.slice(0, 10).map(s => ({
          stage: s.stage || '',
          stageName: s.stageName || '',
          category: s.category || '',
          title: s.title || '',
          createdAt: s.createdAt
            ? new Date(
                s.createdAt.toMillis ? s.createdAt.toMillis() : s.createdAt
              ).toLocaleDateString()
            : '',
        })),
      },
      documentBank: {
        totalDocuments: stats.totalDocuments,
        documentsThisMonth: stats.documentsThisMonth,
        recentDocuments: documents.slice(0, 10).map(d => ({
          fileName: d.fileName || '',
          category: d.category || '',
          fileType: d.fileType || '',
          uploadedAt: d.uploadedAt
            ? new Date(
                d.uploadedAt.toMillis ? d.uploadedAt.toMillis() : d.uploadedAt
              ).toLocaleDateString()
            : '',
        })),
      },
      commercialization: {
        readinessChecklist: readinessChecklist
          ? {
              score: readinessChecklist.score || 0,
              readinessLevel: readinessChecklist.readinessLevel || 'Not Ready',
              totalChecked: readinessChecklist.totalChecked || 0,
              totalItems: readinessChecklist.totalItems || 0,
              lastUpdated: normalizeDateValue(
                readinessChecklist.lastUpdated ||
                  readinessChecklist.updatedAt ||
                  readinessChecklist.timestamp
              ),
            }
          : null,
        gtmPlan: gtmPlan,
        launchChecklist: launchChecklist
          ? {
              score: launchChecklist.score || 0,
              readinessLevel: launchChecklist.readinessLevel || 'Not Ready',
              totalChecked: launchChecklist.totalChecked || 0,
              totalItems: launchChecklist.totalItems || 0,
              lastUpdated: normalizeDateValue(
                launchChecklist.lastUpdated ||
                  launchChecklist.updatedAt ||
                  launchChecklist.timestamp
              ),
            }
          : null,
      },
      statistics: stats,
      summary: {
        keyAchievements: [
          stats.submissionsThisMonth > 0 &&
            `${stats.submissionsThisMonth} new product development submissions`,
          stats.documentsThisMonth > 0 && `${stats.documentsThisMonth} new documents uploaded`,
          readinessChecklist &&
            stats.readinessScore >= 85 &&
            'Commercialisation readiness achieved',
          gtmPlan && stats.gtmProgress === 100 && 'GTM Strategy Plan completed',
          entrepreneurProfile &&
            entrepreneurProfile.overallScore >= 70 &&
            'Entrepreneur profiling indicates strong leadership',
          businessHealth &&
            businessHealth.overallScore >= 70 &&
            'Business health check remains solid',
        ].filter(Boolean),
        areasForAttention: [
          stats.readinessScore < 60 && 'Low commercialisation readiness score',
          gtmPlan && stats.gtmProgress < 50 && 'GTM Strategy Plan incomplete',
          stats.totalSubmissions === 0 && 'No product development submissions',
          entrepreneurProfile &&
            entrepreneurProfile.overallScore > 0 &&
            entrepreneurProfile.overallScore < 60 &&
            'Entrepreneur profiling highlights development areas',
          businessHealth &&
            businessHealth.overallScore > 0 &&
            businessHealth.overallScore < 60 &&
            'Business health check reveals risks',
        ].filter(Boolean),
      },
    };

    return report;
  } catch (error) {
    console.error('Error generating coaching report:', error);
    throw error;
  }
}

/**
 * Generate reports for all clients assigned to a coach
 */
export async function generateCoachMonthlyReports({ coachEmail, month, year }) {
  if (!coachEmail) throw new Error('Coach email is required');

  try {
    // Find all clients assigned to this coach
    const clientsQuery = query(
      collection(firestore, 'clients'),
      where('assignedEmail', '==', coachEmail)
    );
    const clientsSnapshot = await getDocs(clientsQuery);

    const reports = [];
    for (const clientDoc of clientsSnapshot.docs) {
      try {
        const report = await generateMonthlyCoachingReport({
          clientId: clientDoc.id,
          month,
          year,
          coachId: coachEmail,
        });
        reports.push(report);
      } catch (error) {
        console.error(`Failed to generate report for client ${clientDoc.id}:`, error);
        // Continue with other clients
      }
    }

    return reports;
  } catch (error) {
    console.error('Error generating coach reports:', error);
    throw error;
  }
}

/**
 * Export report to JSON format
 */
export function exportReportToJSON(report) {
  return JSON.stringify(report, null, 2);
}

/**
 * Format report for PDF/print display
 */
export function formatReportForDisplay(report) {
  return {
    ...report,
    formattedDate: new Date(report.reportMetadata.generatedAt).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }),
  };
}
